import {
  type ChangeEvent,
  type ComponentType,
  type SVGAttributes,
} from "react";
import { Box, type BoxProps, Text } from "@hightouch/htui";

const visuallyHiddenStyle = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

type ToggleButtonProps = {
  icon?: ComponentType<SVGAttributes<SVGElement>>;
  iconColor?: BoxProps["color"];
  label: string;
  isSelected: boolean;
  isDisabled?: boolean;
  onChange: (checked: boolean) => void;
  /** "checkbox" for multi-select, "radio" for single-select. Defaults to "checkbox". */
  type?: "checkbox" | "radio";
  /** Required for radio inputs so the browser groups them. */
  name?: string;
  value?: string;
  size?: "sm" | "md";
};

export function ToggleButton({
  icon: Icon,
  iconColor,
  label,
  isSelected,
  onChange,
  type = "checkbox",
  isDisabled = false,
  name,
  value,
  size = "md",
}: ToggleButtonProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  return (
    <Box
      as="label"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      gap={2}
      px={3}
      py={1}
      borderRadius="full"
      border="0.5px solid"
      borderColor={isSelected && !isDisabled ? "primary.base" : "base.border"}
      bg={
        isDisabled
          ? "base.background"
          : isSelected
            ? "primary.background"
            : "base.lightBackground"
      }
      cursor={isDisabled ? "not-allowed" : "pointer"}
      opacity={isDisabled ? 0.5 : 1}
      transitionProperty="common"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      _hover={
        isDisabled
          ? {}
          : {
              bg: isSelected ? "primary.background" : "base.background",
            }
      }
      _focusWithin={
        isDisabled
          ? {}
          : {
              boxShadow: "outline",
            }
      }
    >
      <input
        type={type}
        name={name}
        value={value}
        disabled={isDisabled}
        checked={isSelected}
        onChange={handleChange}
        style={visuallyHiddenStyle}
      />
      {Icon && (
        <Box
          as={Icon}
          boxSize={5}
          flexShrink={0}
          color={
            isDisabled
              ? "text.disabled"
              : (iconColor ?? (isSelected ? "primary.base" : "text.secondary"))
          }
        />
      )}
      <Text
        size={size}
        fontWeight="medium"
        color={
          isDisabled
            ? "text.disabled"
            : isSelected
              ? "primary.base"
              : "text.secondary"
        }
      >
        {label}
      </Text>
    </Box>
  );
}
